import type { IconType } from 'react-icons';
import { FaCog, FaGift, FaHistory, FaUserFriends, FaUserTie, FaWallet } from 'react-icons/fa';
import { FiX } from 'react-icons/fi';
import { TbWorld } from 'react-icons/tb';
import { ImProfile } from 'react-icons/im';
import { HiMiniKey } from 'react-icons/hi2';
import { BiLogOutCircle } from 'react-icons/bi';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLogoutMutation } from '@/store/api/authApi';
import { toast } from 'sonner';
import { ApiError } from '@/types/error';
import { logout } from '@/store/slices/authSlice';
import { IoGlobeSharp } from 'react-icons/io5';
import { useParlay } from '@/hooks/useParlay';
import { clearBetCount } from '@/store/slices/currentBetCountSlice';
import { GrAnnounce } from 'react-icons/gr';
import { clearStoredParlaySelections } from '@/utils/parlaySelectionDb';
import { useGetCompetitionsQuery } from '@/store/api/upcomingFixtureApi';
import type { LeagueSport } from '@/pages/LeaguePage/components/SportsTabs';
import { useState } from 'react';
import { CiShare1 } from 'react-icons/ci';

type SidebarIcon = IconType | string;

interface AccountMenuItem {
  label: string;
  icon: SidebarIcon;
  badge?: number;
  href: string;
}

interface SidebarMenuProps {
  open: boolean;
  onClose: () => void;
}

const accountMenus: AccountMenuItem[] = [
  {
    label: 'Agent Details',
    icon: FaUserTie,
    href: '/agent-details',
  },
  {
    label: 'Promotions',
    icon: GrAnnounce,
    href: '/promotions',
  },
  {
    label: 'My Wallet',
    icon: FaWallet,
    href: '/wallet',
  },
  {
    label: 'Balance Overview',
    icon: FaWallet,
    href: '/balance-overview',
  },
  {
    label: 'Current Bets',
    icon: 'icon-list',
    badge: 0,
    href: '/current-bets',
  },
  {
    label: 'Bets History',
    icon: FaHistory,
    href: '/bets-history',
  },
  {
    label: 'Profit & Loss',
    icon: 'icon-profit-loss',
    href: '/profit-loss',
  },
  {
    label: 'Turnover History',
    icon: FaHistory,
    href: '/turnover-history',
  },
  {
    label: 'Account Statement',
    icon: 'icon-list',
    href: '/account-statement',
  },
  {
    label: 'Bonus',
    icon: FaGift,
    href: '/bonus-statement',
  },
  {
    label: 'My Profile',
    icon: ImProfile,
    href: '/profile',
  },
  {
    label: 'Activity Log',
    icon: HiMiniKey,
    href: '/activity-log',
  },
];

const menuIconClassName = 'inline-flex w-6 shrink-0 justify-center leading-none text-[#12263A]';

function MenuIcon({ icon }: { icon: SidebarIcon }) {
  if (typeof icon === 'string') {
    return <i className={`${icon} ${menuIconClassName} !text-[1.25rem]`} />;
  }

  const Icon = icon;
  return <Icon className={`${menuIconClassName} text-[1.125rem]`} />;
}

const sports: { label: LeagueSport; icon: string }[] = [
  { label: 'Cricket', icon: 'icon-cricket' },
  { label: 'Soccer', icon: 'icon-soccer' },
  { label: 'Tennis', icon: 'icon-tennis' },
];

function SportMenuItem({
  sport,
  expanded,
  onToggle,
  onClose,
}: {
  sport: (typeof sports)[number];
  expanded: boolean;
  onToggle: () => void;
  onClose: () => void;
}) {
  const { data, isFetching } = useGetCompetitionsQuery({ sport: sport.label }, { skip: !expanded });
  const competitions = data?.competitions ?? [];

  return (
    <div className="mb-0.5 overflow-hidden rounded-md bg-white">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className={`flex h-[2.5rem] w-full items-center justify-between px-2.5 text-left transition-colors ${
          expanded ? 'bg-primary/35 text-[#12263a]' : 'text-[#12263a] hover:bg-primary/15'
        }`}
      >
        <span className="flex items-center gap-2.5">
          <span className="flex size-6 items-center justify-center rounded-full bg-primary text-[#12263a]">
            <i className={`${sport.icon} text-[1rem]`} />
          </span>
          <span className="text-[0.938rem] font-semibold">{sport.label}</span>
        </span>
        <i
          className={`icon-arrow-down text-sm transition-transform ${expanded ? 'rotate-180' : ''}`}
        />
      </button>

      {expanded && (
        <div className="border-t border-[#d7dee5] bg-[#f8fafb]">
          {isFetching && <p className="px-4 py-2 text-sm text-[#6f8498]">Loading leagues...</p>}
          {!isFetching && competitions.length === 0 && (
            <p className="px-4 py-2 text-sm text-[#6f8498]">No leagues available.</p>
          )}
          {!isFetching &&
            competitions.slice(0, 8).map((competition) => (
              <Link
                key={competition.id}
                to={`/leagues/${sport.label.toLowerCase()}/${competition.id}`}
                state={{ leagueName: competition.name }}
                onClick={onClose}
                className="block truncate border-b border-[#d7dee5] px-4 py-2 text-sm font-medium text-[#526779] last:border-b-0 hover:bg-primary/15 hover:text-[#12263a]"
              >
                {competition.name}
              </Link>
            ))}
          {!isFetching && competitions.length > 8 && (
            <Link
              to="/leagues"
              onClick={onClose}
              className="block px-4 py-2 text-sm font-bold text-[#12263a] hover:bg-primary/15"
            >
              View all {sport.label} leagues
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

export default function SidebarMenu({ open, onClose }: SidebarMenuProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { setIsParlayEnabled } = useParlay();
  const [logoutApi] = useLogoutMutation();
  const [expandedSport, setExpandedSport] = useState<LeagueSport | null>(null);
  const currentBetCount = useAppSelector((state) => state.currentBetCount.totalItems);
  const username = useAppSelector((state) => state.account.profile?.username);
  const currentBetCountText = currentBetCount > 99 ? '99+' : currentBetCount;

  const goTo = (href: string) => {
    navigate(href);
    onClose?.();
  };
  const handleLogout = async () => {
    try {
      await logoutApi().unwrap();
    } catch (error) {
      const message = (error as ApiError)?.data?.message || 'Logout failed';
      toast.error(message);
    } finally {
      localStorage.removeItem('showWelcomeBanner');
      setIsParlayEnabled(false);
      dispatch(logout());
      dispatch(clearBetCount());
      clearStoredParlaySelections();
      navigate('/', { replace: true });
      onClose?.();
    }
  };

  return (
    <div
      className={`fixed inset-y-0 left-1/2 z-[140] w-full max-w-[30rem] p-3.5 -translate-x-1/2 overflow-hidden 
        bg-black/50 backdrop-blur-sm ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
      onClick={onClose}
    >
      <aside
        onClick={(event) => event.stopPropagation()}
        className={`rounded-lg relative left-0 top-0 flex h-full w-full flex-col overflow-hidden bg-[#e9eff3] px-2.5 py-2 shadow-xl transition-transform duration-300 ease-in-out 
          md:px-3 ${open ? 'translate-x-0' : '-translate-x-full'}`}
      >
        {/* Header / brand */}
        <div className="flex shrink-0 items-center justify-between border-b border-[#cbd9e0] px-1 pb-2">
          <Link to="/" onClick={onClose} className="flex items-center">
            <img src="/images/logo-black.webp" alt="Velki" className="w-[7.5rem]" />
          </Link>

          <button onClick={onClose} className="flex size-7 items-center justify-center">
            <FiX className="text-[1.375rem] text-[#12263A]" />
          </button>
        </div>

        <div className="mt-2 flex shrink-0 items-center gap-2.5 rounded-md bg-primary px-2.5 py-2 text-black">
          <span className="flex size-6 items-center justify-center rounded-full bg-black text-xs font-bold text-primary">
            {username?.charAt(0).toUpperCase() ?? 'U'}
          </span>
          <span className="truncate text-[0.938rem] font-bold">{username ?? 'User'}</span>
        </div>

        <div className="scrollbar-hidden min-h-0 flex-1 overflow-y-auto pt-2">
          {/* Sports are intentionally first, matching the reference navigation structure. */}
          <nav className="mb-2.5" aria-label="Sports">
            <p className="mb-1 px-1 text-[0.688rem] font-bold uppercase tracking-wide text-[#6f8498]">
              Sports
            </p>
            {sports.map((sport) => (
              <SportMenuItem
                key={sport.label}
                sport={sport}
                expanded={expandedSport === sport.label}
                onToggle={() =>
                  setExpandedSport((current) => (current === sport.label ? null : sport.label))
                }
                onClose={onClose}
              />
            ))}
            <Link
              to="/casino"
              onClick={onClose}
              className="flex h-[2.5rem] items-center justify-between rounded-md bg-white px-2.5 text-[#12263a] hover:bg-primary/15"
            >
              <span className="flex items-center gap-2.5">
                <span className="flex size-6 items-center justify-center rounded-full bg-primary text-[#12263a]">
                  <i className="icon-game-hall-table text-[1.5rem]" />
                </span>
                <span className="text-[0.938rem] font-semibold">Casino</span>
              </span>
              <i className="icon-arrow-down -rotate-90 text-sm" />
            </Link>
          </nav>

          {/* Refer & Earn */}
          <div className="mb-2.5 overflow-hidden rounded-md bg-white">
            <Link
              to={'/newreferral'}
              onClick={onClose}
              className={`flex h-[2.75rem] w-full items-center gap-2 px-3 ${
                pathname === '/newreferral' ? 'bg-primary/35' : ''
              }`}
            >
              <FaUserFriends className={menuIconClassName} />

              <span className="flex-1 text-left text-[0.938rem] text-[#12263A]">Refer & Earn</span>

              <i className="icon-arrow-down opacity-50 -rotate-90 text-sm" />
            </Link>
          </div>

          {/* External links - replace the placeholder hrefs when the destinations are available. */}
          <div className="mb-2.5 overflow-hidden rounded-md bg-white">
            <a
              href="#"
              onClick={onClose}
              className="flex h-[2.75rem] w-full items-center gap-2 border-b border-[#D7DEE5] px-3"
            >
              <span className="flex-1 text-left text-[0.938rem] text-[#12263A]">Agent List</span>
              <CiShare1 className="text-xl text-[#12263A]" />
            </a>
            <a
              href="#"
              onClick={onClose}
              className="flex h-[2.75rem] w-full items-center gap-2 px-3"
            >
              <span className="flex-1 text-left text-[0.938rem] text-[#12263A]">Quiz</span>
              <CiShare1 className="text-xl text-[#12263A]" />
            </a>
          </div>

          {/* Velki account options */}
          <p className="mb-1 px-1 text-[0.688rem] font-bold uppercase tracking-wide text-[#6f8498]">
            OTHER MENU
          </p>
          <div className="overflow-hidden rounded-md bg-white">
            {accountMenus.map((item, index) => {
              const isActive = pathname === item.href;

              return (
                <button
                  key={item.label}
                  className={`flex h-[2.75rem] w-full items-center gap-2 px-3 ${
                    index !== accountMenus.length - 1 ? 'border-b border-[#D7DEE5]' : ''
                  } ${isActive ? 'bg-primary/35' : ''}`}
                  onClick={() => goTo(item.href)}
                >
                  <MenuIcon icon={item.icon} />

                  <span className="flex flex-1 items-center text-[0.938rem] text-[#12263A]">
                    {item.label}

                    {item.badge !== undefined && (
                      <span className="ml-2 flex items-center justify-center rounded bg-[#F3C400] px-1.5 text-sm font-medium text-black">
                        {currentBetCountText}
                      </span>
                    )}
                  </span>

                  <i className="icon-arrow-down opacity-50 -rotate-90 text-sm" />
                </button>
              );
            })}
          </div>

          {/* Settings */}
          <div className="mt-2.5 overflow-hidden rounded-md bg-white">
            <button
              onClick={() => goTo('/setting')}
              className={`flex h-[2.75rem] w-full items-center gap-2 px-3 ${
                pathname === '/setting' ? 'bg-primary/35' : ''
              }`}
            >
              <FaCog className={menuIconClassName} />

              <span className="flex-1 text-left text-[0.938rem] font-medium text-[#12263A]">
                Setting
              </span>

              <i className="icon-arrow-down opacity-50 -rotate-90 text-sm" />
            </button>
          </div>

          {/* Logout */}
          <div className="mt-2.5 overflow-hidden rounded-md bg-[#D6E0E7]">
            <button
              onClick={handleLogout}
              className="flex h-[2.75rem] w-full items-center gap-2 px-3"
            >
              <BiLogOutCircle className="size-[1.6rem]" />

              <span className="flex-1 text-left text-[0.938rem] font-medium text-[#12263A]">
                Logout
              </span>

              <i className="icon-arrow-down opacity-50 -rotate-90 text-sm" />
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}
